import { Mnemonic, PrivateKey } from '@bsv/sdk'
import { accessTokens } from '../api/accessTokenMgmt'
import { CLIENT_SIGNING_READY_EVENT } from './clientSigningFeature'
import {
    getClientShare,
    getLocalSelfCustodyPayloadStatus,
    getOrCreateLocalDeviceId,
    persistSelfCustodyWalletForDevice,
    walletRootIdentityPublicKey,
} from './bsv-signing'

// Browser-made wallets for new signups ("Set up your wallet so we can send you 100K sats").
// The keys never leave this device: Treechat stores the public identity and the server
// half of the device's wallet-access key, like any client-signing device.

export type WelcomeGiftStep = 'download' | 'passkey' | 'file_check'

export interface LocalWalletGift {
    step: WelcomeGiftStep
    status: 'pending' | 'sending' | 'sent' | 'failed'
    amount_sats: number
    tx_id: string | null
}

export interface LocalWalletStatus {
    registered: boolean
    payment_address: string | null
    backup_file_saved: boolean
    passkey_verified: boolean
    backup_file_verified: boolean
    backed_up: boolean
    wizard_closed: boolean
    gift_sats: number
    gift_step_sats: Record<WelcomeGiftStep, number>
    gifts: LocalWalletGift[]
    introductions_team_id?: string | null
}

/**
 * What this account still has to do, and what it is owed. The signup setup is the
 * download plus (where passkeys work) the passkey; checking the saved file is a
 * separate optional reward offered afterwards.
 */
export function walletSetupRemaining(status: LocalWalletStatus, passkeysAvailable: boolean) {
    const earned: WelcomeGiftStep[] = []
    if (status.backup_file_saved) earned.push('download')
    if (status.passkey_verified) earned.push('passkey')
    if (status.backup_file_verified) earned.push('file_check')
    const giftFor = (step: WelcomeGiftStep) => status.gifts.find(gift => gift.step === step)
    const stepSats = (step: WelcomeGiftStep) => status.gift_step_sats?.[step] ?? 0
    const unpaid = earned.filter(step => {
        const gift = giftFor(step)
        return stepSats(step) > 0 && (!gift || gift.status === 'pending')
    })
    return {
        stepsLeft: !status.backup_file_saved || (passkeysAvailable && !status.passkey_verified),
        giftUnclaimed: unpaid.length > 0,
        fileCheckOffered:
            status.backup_file_saved && !status.backup_file_verified && stepSats('file_check') > 0,
        giftFor,
    }
}

export type WalletSafetyRowState = 'done' | 'todo' | 'checking' | 'unavailable'
export type WalletSafetyAction = 'setup' | 'fileCheck' | 'unlock' | null

export interface WalletSafetyRow {
    key: 'file' | 'passkey' | 'device'
    state: WalletSafetyRowState
    action: WalletSafetyAction
    /** Sats this row still earns, or 0. */
    rewardSats: number
}

/**
 * The Wallet modal's "Wallet safety" card: the wallet file, the passkey and this
 * device, each done or with the one action that finishes it. deviceHasKeys is
 * undefined while this device's keys are still being read.
 */
export function walletSafetyRows(
    status: LocalWalletStatus,
    deviceHasKeys: boolean | undefined,
    passkeysAvailable: boolean,
): WalletSafetyRow[] {
    const sats = (step: WelcomeGiftStep) => status.gift_step_sats?.[step] ?? 0
    const file: WalletSafetyRow = !status.backup_file_saved
        ? { key: 'file', state: 'todo', action: 'setup', rewardSats: sats('download') }
        : !status.backup_file_verified
        ? { key: 'file', state: 'todo', action: 'fileCheck', rewardSats: sats('file_check') }
        : { key: 'file', state: 'done', action: null, rewardSats: 0 }
    const passkey: WalletSafetyRow = status.passkey_verified
        ? { key: 'passkey', state: 'done', action: null, rewardSats: 0 }
        : !passkeysAvailable
        ? { key: 'passkey', state: 'unavailable', action: null, rewardSats: 0 }
        : { key: 'passkey', state: 'todo', action: 'setup', rewardSats: sats('passkey') }
    const device: WalletSafetyRow =
        deviceHasKeys === undefined
            ? { key: 'device', state: 'checking', action: null, rewardSats: 0 }
            : deviceHasKeys
            ? { key: 'device', state: 'done', action: null, rewardSats: 0 }
            : { key: 'device', state: 'todo', action: 'unlock', rewardSats: 0 }
    return [file, passkey, device]
}

/** Rejects when work takes longer than ms, so a hung request can be retried. */
export function withTimeout<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(message)), ms)
        work.then(
            value => {
                clearTimeout(timer)
                resolve(value)
            },
            error => {
                clearTimeout(timer)
                reject(error)
            },
        )
    })
}

export class NotLocalWalletAccountError extends Error {}

/** Dispatch to reopen the setup, e.g. from the Wallet modal's backup card. */
export const OPEN_WALLET_SETUP_EVENT = 'treechat:open-wallet-setup'

async function localWalletRequest<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${window.knovApiUrl}/api/v1/local_wallet${path}`, {
        method: body === undefined ? 'GET' : 'POST',
        headers: {
            ...accessTokens(),
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        cache: 'no-store',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })
    if (response.status === 404) {
        throw new NotLocalWalletAccountError('This account does not use a browser wallet.')
    }
    if (!response.ok) {
        // Never log the request: registration carries the server half of the wallet key.
        const result = await response.json().catch(() => ({}))
        throw new Error(result.error || 'Wallet request failed. Please try again.')
    }
    return response.json()
}

export const loadLocalWalletStatus = () => localWalletRequest<LocalWalletStatus>('')
// iOS Safari treats the wallet file download like leaving the page: while its "Do you
// want to download" prompt is open it cancels the page's requests and reports them
// as "Load failed" (a TypeError). Requests that are safe to repeat go out again after
// a wait.
export async function retryNetworkFailure<T>(
    request: () => Promise<T>,
    { attempts = 3, delayMs = 600, maxDelayMs = Infinity } = {},
): Promise<T> {
    for (let attempt = 1; ; attempt += 1) {
        try {
            return await request()
        } catch (error) {
            if (!(error instanceof TypeError) || attempt >= attempts) throw error
            await new Promise(resolve =>
                setTimeout(resolve, Math.min(delayMs * attempt, maxDelayMs)),
            )
        }
    }
}

// The page can't tell when Safari's download prompt closes, so the requests made right
// after the download keep trying for about 45 seconds (the prompt is open until the
// person answers it).
export const UNTIL_DOWNLOAD_PROMPT_CLOSES = { attempts: 15, delayMs: 600, maxDelayMs: 4000 }

/** The status, asked again while Safari's download prompt is cancelling requests. */
export const loadLocalWalletStatusPatiently = () =>
    retryNetworkFailure(loadLocalWalletStatus, UNTIL_DOWNLOAD_PROMPT_CLOSES)

export const recordWalletFileSaved = () =>
    retryNetworkFailure(() => localWalletRequest<LocalWalletStatus>('/backup_saved', {}))
export const recordWalletSetupClosed = () =>
    localWalletRequest<LocalWalletStatus>('/wizard_closed', {})
export const claimWelcomeGift = () =>
    retryNetworkFailure(
        () => localWalletRequest<LocalWalletStatus>('/gift', {}),
        UNTIL_DOWNLOAD_PROMPT_CLOSES,
    )
/** Only for a wallet that was never backed up and is gone from its device. */
export const resetUnbackedLocalWallet = () => localWalletRequest<LocalWalletStatus>('/reset', {})

/** A new wallet in the same JSON shape Treechat's server wallets use. */
export function generateLocalWalletText(): string {
    const entropy = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    return JSON.stringify({
        ordPk: PrivateKey.fromRandom().toWif(),
        payPk: PrivateKey.fromRandom().toWif(),
        treechatRootMnemonic: Mnemonic.fromEntropy(entropy).toString(),
    })
}

export type LocalWalletDeviceState = 'ready' | 'needs_restore'

export const LOCAL_WALLET_NEEDS_NEWER_BROWSER =
    'This browser is too old to make your wallet safely. Update it, or open Treechat in another browser.'

/**
 * Makes sure this account has its browser wallet and that this device holds it.
 * A wallet Treechat has never seen is safe to replace, so an interrupted setup simply
 * starts over; once registered, only a backup can bring it to another device.
 */
export async function ensureLocalWallet(
    userId: string,
    status: LocalWalletStatus,
): Promise<{ state: LocalWalletDeviceState; status: LocalWalletStatus }> {
    // Two tabs must not each make a wallet: the second would overwrite the first one's
    // keys on this device after Treechat registered them.
    const run = async () => {
        const fresh = await loadLocalWalletStatus()
        return ensureLocalWalletUnlocked(userId, fresh)
    }
    if (navigator.locks?.request) {
        return navigator.locks.request(`treechat-local-wallet:${userId}`, run)
    }
    if (status.registered) return ensureLocalWalletUnlocked(userId, status)
    // Without a cross-tab lock, two tabs could each make a wallet and the later one would
    // overwrite the registered wallet's only keys on this device. Don't make one unlocked.
    throw new Error(LOCAL_WALLET_NEEDS_NEWER_BROWSER)
}

async function ensureLocalWalletUnlocked(
    userId: string,
    status: LocalWalletStatus,
): Promise<{ state: LocalWalletDeviceState; status: LocalWalletStatus }> {
    const payload = await getLocalSelfCustodyPayloadStatus(userId)
    if (status.registered) {
        // The device must hold the registered wallet, not one that lost a race to register.
        const local = await getClientShare(userId)
        const holdsIt = payload === 'compatible' && local?.address === status.payment_address
        return { state: holdsIt ? 'ready' : 'needs_restore', status }
    }

    const walletText = generateLocalWalletText()
    const rootIdentityKey = walletRootIdentityPublicKey(walletText)
    if (!rootIdentityKey) throw new Error('Your wallet could not be created. Please try again.')
    const deviceId = await getOrCreateLocalDeviceId(userId)
    // Saved on the device before Treechat hears of it, so a lost response never leaves
    // a registered wallet that no device holds.
    const persisted = await persistSelfCustodyWalletForDevice(userId, deviceId, walletText)
    const registered = await localWalletRequest<LocalWalletStatus>('', {
        device_id: deviceId,
        treechat_share: persisted.shares.treechatShare,
        payment_address: persisted.walletIdentity.paymentAddress,
        ordinals_address: persisted.walletIdentity.ordinalsAddress,
        root_identity_key: rootIdentityKey,
    })
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(CLIENT_SIGNING_READY_EVENT))
    return { state: 'ready', status: registered }
}
